"use client";

import React from "react";
import { motion } from "framer-motion";
import { Home, ArrowLeft, Search } from "lucide-react";
import { useRouter } from "next/navigation";
import Link from "next/link";

export default function NotFoundPage() {
  const router = useRouter();
  return (
    <div className="min-h-screen bg-background-primary text-text-primary flex items-center justify-center px-6! relative overflow-hidden">
      <motion.div
        className="absolute w-[500px] h-[500px] bg-cyan-500/20 rounded-full blur-3xl"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.4, 0.7, 0.4],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {[...Array(8)].map((_, index) => (
        <motion.div
          key={index}
          className="absolute border border-cyan-400/20 rounded-full"
          style={{
            width: `${80 + index * 40}px`,
            height: `${80 + index * 40}px`,
          }}
          animate={{
            rotate: 360,
            y: [0, -20, 0],
          }}
          transition={{
            rotate: {
              duration: 20 + index * 5,
              repeat: Infinity,
              ease: "linear",
            },
            y: {
              duration: 3 + index,
              repeat: Infinity,
              ease: "easeInOut",
            },
          }}
        />
      ))}

      <div className="relative z-10 text-center max-w-2xl">
        <motion.h1
          className="text-[120px] md:text-[180px] font-black leading-none bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent"
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          404
        </motion.h1>

        <motion.div
          animate={{
            y: [0, -12, 0],
            rotate: [0, 5, -5, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
          }}
          className="flex justify-center mb-6!"
        >
          <div className="p-5! rounded-full bg-cyan-500/10 border border-cyan-400/30 backdrop-blur-xl">
            <Search className="w-10 h-10 text-cyan-400" />
          </div>
        </motion.div>

        <motion.h2 className="text-3xl md:text-5xl font-bold mb-4!" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          Oops! Page Not Found
        </motion.h2>

        <motion.p className="text-text-secondary text-lg mb-10! leading-relaxed" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          The page you are looking for might have been removed, renamed, or is temporarily unavailable.
        </motion.p>

        <motion.div className="flex flex-col sm:flex-row gap-4 justify-center" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <button
            onClick={() => router.back()}
            className="group px-6! py-3! rounded-2xl border border-border-primary bg-white/5 backdrop-blur-xl hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Go Back
          </button>

          <Link
            href="/"
            className="group px-6! py-3! rounded-2xl bg-cyan-500 hover:bg-cyan-400 text-text-primary font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/20 cursor-pointer"
          >
            <Home className="w-5 h-5 group-hover:scale-110 transition-transform" />
            Back To Home
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
